import clinicsApi from './clinicsApi';
import {
  normalizeAppointment,
  normalizeAppointmentList,
} from '../contracts/clinicContracts';
import type {
  Appointment,
  AppointmentApiResponse,
  AppointmentCreatePayload,
  AppointmentListApiResponse,
  AppointmentListResponse,
  AppointmentUpdatePayload,
  Doctor,
  DoctorListResponse,
  DoctorPayload,
  Patient,
  PatientListResponse,
} from '../contracts/clinicContracts';

export {
  APPOINTMENT_TYPES,
  getAppointmentChiefComplaint,
  getAppointmentDurationMinutes,
  getAppointmentNotes,
  isAppointmentType,
  isPatientStatus,
  PATIENT_STATUSES,
} from '../contracts/clinicContracts';
export type {
  Appointment,
  AppointmentCreatePayload,
  AppointmentDetails,
  AppointmentListResponse,
  AppointmentType,
  AppointmentUpdatePayload,
  Doctor,
  DoctorListResponse,
  DoctorPayload,
  DoctorVacation,
  DoctorWorkday,
  Patient,
  PatientListResponse,
  PatientStatus,
  PatientTag,
} from '../contracts/clinicContracts';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export interface ClinicRoom {
  id: string;
  name: string;
  resource_type: string;
  capacity: number;
  description?: string;
  color_code: string;
  is_active: boolean;
}

export interface PatientFile {
  id: number;
  patient_id: number;
  appointment_id: number | null;
  file_name: string;
  file_type: string | null;
  file_size_bytes: number | null;
  folder_id?: number | null;
  uploaded_at: string;
  processing_status: 'pending' | 'completed' | 'error';
  extracted_metadata: any | null;
  error_message: string | null;
}

export interface ChatConversation {
  id: number;
  patient_id: number;
  title: string | null;
  is_saved: boolean;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface ChatConversationListResponse {
  conversations: ChatConversation[];
  total: number;
  page: number;
  per_page: number;
}

export interface ChatMessage {
  id: number;
  conversation_id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  reasoning?: string | null;
  consulted_data?: Record<string, any> | null;
  consulted_documents?: any[] | null;
  created_at: string;
}

export interface ChatMessageListResponse {
  messages: ChatMessage[];
  total: number;
  page: number;
  per_page: number;
}

export interface DashboardStats {
  total_patients: number;
  active_patients: number;
  total_appointments: number;
  appointments_today: number;
  pending_appointments: number;
  new_patients_today: number;
}

export interface ClinicHoliday {
  id: number;
  fecha: string;
  esta_abierto: boolean;
  hora_apertura_especial: string | null;
  hora_cierre_especial: string | null;
  descripcion: string | null;
}

export interface RecentPatient {
  id: number;
  full_name: string;
  status: string;
  diagnosis: string | null;
  last_visit: string | null;
  next_appointment: string | null;
}

export interface DashboardResponse {
  stats: DashboardStats;
  recent_patients: RecentPatient[];
}

export interface ClinicChatStats {
  total_messages: number;
  total_user_messages: number;
  total_assistant_messages: number;
  total_threads: number;
  active_threads: number;
  avg_messages_per_patient: number;
  avg_operator_response_time_ms: number;
  timeline: { date: string; messages: number }[];
  top_patients: { patient_id: number; full_name: string; message_count: number }[];
}

// ═══════════════════════════════════════════════════════════════
// Service Methods
// ═══════════════════════════════════════════════════════════════

export const clinicsService = {
  // Dashboard
  getDashboard: async (): Promise<DashboardResponse> => {
    const { data } = await clinicsApi.get('/clinics/dashboard');
    return data;
  },

  // Rooms
  getClinicRooms: async (): Promise<ClinicRoom[]> => {
    const { data } = await clinicsApi.get('/clinics/rooms');
    return data;
  },

  createClinicRoom: async (payload: Partial<ClinicRoom>): Promise<ClinicRoom> => {
    const { data } = await clinicsApi.post('/clinics/rooms', payload);
    return data;
  },

  updateClinicRoom: async (id: string, payload: Partial<ClinicRoom>): Promise<ClinicRoom> => {
    const { data } = await clinicsApi.put(`/clinics/rooms/${id}`, payload);
    return data;
  },

  // Patients
  getPatients: async (params?: { page?: number; per_page?: number; search?: string; status?: string; tag_ids?: string }): Promise<PatientListResponse> => {
    const { data } = await clinicsApi.get('/clinics/patients', { params });
    return data;
  },

  getPatientById: async (id: number): Promise<Patient> => {
    const { data } = await clinicsApi.get(`/clinics/patients/${id}`);
    return data;
  },

  createPatient: async (payload: Partial<Patient>): Promise<Patient> => {
    const { data } = await clinicsApi.post('/clinics/patients', payload);
    return data;
  },

  updatePatient: async (id: number, payload: Partial<Patient>): Promise<Patient> => {
    const { data } = await clinicsApi.put(`/clinics/patients/${id}`, payload);
    return data;
  },

  deletePatient: async (id: number): Promise<void> => {
    await clinicsApi.delete(`/clinics/patients/${id}`);
  },

  bindPatientToChat: async (patientId: number, payload: { chat_channel: string; chat_customer_id: string }): Promise<void> => {
    await clinicsApi.post(`/clinics/patients/${patientId}/chat-bindings`, payload);
  },

  // Appointments
  getAppointments: async (params?: { page?: number; per_page?: number; patient_id?: number; tag_ids?: string; date_from?: string; date_to?: string; room_id?: string; status?: string }): Promise<AppointmentListResponse> => {
    const { data } = await clinicsApi.get<AppointmentListApiResponse>('/clinics/appointments', { params });
    return normalizeAppointmentList(data);
  },

  createAppointment: async (payload: AppointmentCreatePayload): Promise<Appointment> => {
    const { data } = await clinicsApi.post<AppointmentApiResponse>('/clinics/appointments', payload);
    return normalizeAppointment(data);
  },

  updateAppointment: async (id: number, payload: AppointmentUpdatePayload): Promise<Appointment> => {
    const { data } = await clinicsApi.put<AppointmentApiResponse>(`/clinics/appointments/${id}`, payload);
    return normalizeAppointment(data);
  },

  deleteAppointment: async (id: number): Promise<void> => {
    await clinicsApi.delete(`/clinics/appointments/${id}`);
  },

  // Chat/Messages helpers
  getChatMessages: async (conversationId: number): Promise<ChatMessage[]> => {
    const response = await clinicsApi.get(`/clinics/chat-conversations/${conversationId}/messages`);
    return response.data;
  },
  
  getChatStats: async (timeRange?: string): Promise<ClinicChatStats> => {
    const params = new URLSearchParams();
    if (timeRange) params.append('time_range', timeRange);
    
    const url = `/clinics/chat-conversations/stats${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await clinicsApi.get(url);
    return response.data;
  },

  // Files
  getPatientFiles: async (patientId: number): Promise<PatientFile[]> => {
    const { data } = await clinicsApi.get(`/clinics/patients/${patientId}/files`);
    return data;
  },

  uploadFile: async (patientId: number, file: File, appointmentId?: number, folderId?: number): Promise<PatientFile> => {
    const formData = new FormData();
    formData.append('file', file);
    
    let url = `/clinics/patients/${patientId}/files`;
    const params = new URLSearchParams();
    if (appointmentId) params.append('appointment_id', String(appointmentId));
    if (folderId) params.append('folder_id', String(folderId));
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const { data } = await clinicsApi.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  deleteFile: async (patientId: number, fileId: number): Promise<void> => {
    await clinicsApi.delete(`/clinics/patients/${patientId}/files/${fileId}`);
  },

  downloadFile: async (patientId: number, fileId: number): Promise<Blob> => {
    const { data } = await clinicsApi.get(
      `/clinics/patients/${patientId}/files/${fileId}/download`,
      { responseType: 'blob' },
    );
    return data;
  },

  // Tags
  addTagToPatient: async (patientId: number, tagId: number): Promise<void> => {
    await clinicsApi.post(`/clinics/patients/${patientId}/tags/${tagId}`);
  },
  removeTagFromPatient: async (patientId: number, tagId: number): Promise<void> => {
    await clinicsApi.delete(`/clinics/patients/${patientId}/tags/${tagId}`);
  },

  // Folders
  getPatientFolders: async (patientId: number): Promise<any[]> => {
    const { data } = await clinicsApi.get(`/clinics/patients/${patientId}/folders`);
    return data;
  },
  createPatientFolder: async (patientId: number, payload: { name: string; parent_id?: number | null }): Promise<any> => {
    const { data } = await clinicsApi.post(`/clinics/patients/${patientId}/folders`, payload);
    return data;
  },
  updatePatientFolder: async (patientId: number, folderId: number, payload: { name?: string; parent_id?: number | null }): Promise<any> => {
    const { data } = await clinicsApi.put(`/clinics/patients/${patientId}/folders/${folderId}`, payload);
    return data;
  },
  deletePatientFolder: async (patientId: number, folderId: number): Promise<void> => {
    await clinicsApi.delete(`/clinics/patients/${patientId}/folders/${folderId}`);
  },

  /**
   * Move a file to a different folder (or to root if folderId is null).
   * Calls PATCH /patients/{patientId}/files/{fileId} with { folder_id }.
   */
  moveFile: async (patientId: number, fileId: number, folderId: number | null): Promise<PatientFile> => {
    const { data } = await clinicsApi.patch(`/clinics/patients/${patientId}/files/${fileId}`, { folder_id: folderId });
    return data;
  },

  /**
   * Move a folder to a different parent (or to root if parentId is null).
   * Reuses the existing PUT /patients/{patientId}/folders/{folderId} endpoint.
   */
  moveFolder: async (patientId: number, folderId: number, parentId: number | null): Promise<any> => {
    const { data } = await clinicsApi.put(`/clinics/patients/${patientId}/folders/${folderId}`, { parent_id: parentId });
    return data;
  },



  // Chat
  getConversations: async (
    patientId: number, 
    params?: { 
      page?: number; 
      per_page?: number;
      search?: string;
      date_from?: string;
      date_to?: string;
      is_saved?: boolean;
    }
  ): Promise<ChatConversationListResponse> => {
    const { data } = await clinicsApi.get(`/clinics/patients/${patientId}/chat/conversations`, { params });
    return data;
  },

  updateConversation: async (
    patientId: number, 
    conversationId: number, 
    payload: { title?: string; is_saved?: boolean }
  ): Promise<ChatConversation> => {
    const { data } = await clinicsApi.patch(`/clinics/patients/${patientId}/chat/conversations/${conversationId}`, payload);
    return data;
  },

  createConversation: async (patientId: number, title?: string): Promise<ChatConversation> => {
    const { data } = await clinicsApi.post(`/clinics/patients/${patientId}/chat/conversations`, { title });
    return data;
  },

  getMessages: async (patientId: number, conversationId: number, params?: { page?: number; per_page?: number }): Promise<ChatMessageListResponse> => {
    const { data } = await clinicsApi.get(`/clinics/patients/${patientId}/chat/conversations/${conversationId}/messages`, { params });
    return data;
  },

  generatePatientSummary: async (patientId: number): Promise<{ summary: string }> => {
    const { data } = await clinicsApi.post(`/clinics/patients/${patientId}/rag/summary`);
    return data;
  },

  sendMessage: async (patientId: number, conversationId: number, content: string): Promise<ChatMessage> => {
    const { data } = await clinicsApi.post(`/clinics/patients/${patientId}/chat/conversations/${conversationId}/messages`, { content });
    return data;
  },

  // Helper for SSE streaming
  getStreamUrl: (patientId: number, conversationId: number) => {
    return `${import.meta.env.VITE_CLINICS_API_URL || '/api/v1'}/clinics/patients/${patientId}/chat/conversations/${conversationId}/messages/stream`;
  },

  // Doctors
  getDoctors: async (params?: { page?: number; per_page?: number }): Promise<DoctorListResponse> => {
    const { data } = await clinicsApi.get('/clinics/doctors', { params });
    return data;
  },
  
  createDoctor: async (payload: DoctorPayload): Promise<Doctor> => {
    const { data } = await clinicsApi.post('/clinics/doctors', payload);
    return data;
  },
  
  updateDoctor: async (doctorId: number, payload: Partial<DoctorPayload>): Promise<Doctor> => {
    const { data } = await clinicsApi.put(`/clinics/doctors/${doctorId}`, payload);
    return data;
  },
  
  deleteDoctor: async (doctorId: number): Promise<void> => {
    await clinicsApi.delete(`/clinics/doctors/${doctorId}`);
  }
};
